import type { Locale } from '@kanto/shared';
import type { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLogout } from '../api/hooks';
import { AVAILABLE_LOCALES, LOCALE_NAMES, useLocale, useT } from '../lib/i18n';

/**
 * What language the instance speaks, and the way out.
 *
 * One component because there are now two places that carry these controls — the
 * rail on a wide screen, the top bar below it — and they have to offer the *same*
 * thing. Two copies would eventually disagree, and the one that disagreed would be
 * the one nobody looks at; this is the reasoning `Brand` already carries.
 *
 * The language switcher lists both languages with the current one marked, rather
 * than one button toggling between them. A toggle never says what it will switch
 * to, and it has nowhere to put a third language — which is the only kind of
 * change this list is likely to see.
 */
export function SessionControls({
  orientation = 'horizontal',
}: {
  /** `vertical` is the 68 px rail, where nothing fits side by side. */
  orientation?: 'horizontal' | 'vertical';
}): ReactElement {
  const t = useT();
  const { locale, setLocale } = useLocale();
  const logout = useLogout();
  const navigate = useNavigate();

  const vertical = orientation === 'vertical';

  const signOut = (): void => {
    // Navigate on success rather than immediately: if the call fails the session
    // is still open, and moving to the sign-in screen would claim otherwise.
    logout.mutate(undefined, { onSuccess: () => void navigate('/login', { replace: true }) });
  };

  return (
    <div className={`flex shrink-0 items-center gap-2 ${vertical ? 'flex-col' : ''}`}>
      {/* A radio group and not a set of buttons: these are two states of one
          setting, and that is what a screen reader should hear. */}
      <div
        role="radiogroup"
        aria-label={t('topbar.language')}
        className={`flex items-center rounded-lg border border-line bg-surface p-0.5 ${
          vertical ? 'flex-col' : ''
        }`}
      >
        {AVAILABLE_LOCALES.map((code: Locale) => (
          <button
            key={code}
            type="button"
            role="radio"
            aria-checked={code === locale}
            // The written-out name is the accessible name: "EN" is read out as
            // a word by some screen readers and as an abbreviation by others.
            aria-label={LOCALE_NAMES[code]}
            onClick={() => setLocale(code)}
            className={`rounded-md text-sm font-medium transition-colors ${
              vertical ? 'min-h-8 w-11' : 'min-h-10 px-2.5'
            } ${code === locale ? 'bg-accent-soft text-accent' : 'text-ink-500 hover:text-ink-900'}`}
          >
            {code.toUpperCase()}
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={signOut}
        disabled={logout.isPending}
        aria-label={t('topbar.signOut')}
        className={`flex items-center justify-center gap-2 rounded-lg border border-line bg-surface text-sm font-medium text-ink-700 transition-colors hover:border-line-strong hover:text-ink-900 disabled:opacity-60 ${
          vertical ? 'min-h-11 w-11' : 'min-h-11 shrink-0 px-3'
        }`}
      >
        <svg
          viewBox="0 0 24 24"
          className="size-4 shrink-0"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
        </svg>
        {/* The label is written out from `sm` upwards and only there: at 390 px
            the bar has to hold a name, two languages and this, and the
            door-and-arrow is the one icon here that needs no reading. The
            `aria-label` above says it in every case. */}
        {!vertical && <span className="hidden sm:inline">{t('topbar.signOut')}</span>}
      </button>
    </div>
  );
}
